import { createAsyncThunk } from "@reduxjs/toolkit"
import FileDownload from "js-file-download"

import i18n from "../../../i18n"
import { ErrorsActions } from "../../../Redux/redux/errors.slice"
import { getApiErrorMessage, handleApiError } from "../../../Utils/thunkHelpers"
import * as services from "../api/services"
import { expandGroupsToCategories } from "../pages/AllPage/constants/categoryGroups"
import { DISPUTES_SLICE_NAME as SLICE_NAME } from "./disputes.constants"

// Error policy, per `.claude/rules/redux-toolkit.md`:
// - apisauce never rejects — a network failure arrives as `{ ok: false, problem }` — so
//   nothing here wraps a service call in try/catch. The one exception is re-parsing a
//   blob error body, which really can throw; it is guarded where it happens.
// - The failure is the guard: `if (!response.ok)` → `handleApiError` (the one global
//   notification channel) → `rejectWithValue(response.data)`. The success path is what
//   the thunk falls through to. Nothing raises its own error toast.
// - A thunk skips `handleApiError` only where the screen already reports the failure
//   itself (the saga's old `withoutErrorNotification`); each such case says so below and
//   still rejects.
// - No thunk shows a success message. What a merchant is told after an action belongs to
//   the screen that triggered it: the component awaits `dispatch(thunk()).unwrap()` and
//   notifies from its own `App.useApp()` instance, which the thunk has no access to.

export const getDisputes = createAsyncThunk(
  `${SLICE_NAME}/get_disputes`,
  // Silent: the table renders its own error/empty state from `list.error`.
  async (payload, { rejectWithValue }) => {
    const filters = payload?.filters ?? {}
    const response = await services.getDisputes(filters)
    if (!response.ok || !response.data) {
      return rejectWithValue(response.data)
    }
    return {
      results: response.data.results ?? [],
      count: response.data.count ?? 0,
      statistics: response.data.statistics ?? {}
    }
  }
)

export const getDisputesStatistics = createAsyncThunk(
  `${SLICE_NAME}/get_disputes_statistics`,
  // Silent: the cards and the sidebar badge fall back to their empty state.
  async (_, { rejectWithValue }) => {
    const response = await services.getDisputesStatistics()
    if (!response.ok || !response.data) {
      return rejectWithValue(response.data)
    }
    return { statistics: response.data.statistics ?? {} }
  }
)

// CSV export. "Export all" sends no filters; "Export as filtered" pulls the
// active filter set from state so the file matches what's on screen. The blob
// is handed to FileDownload, which triggers the browser download directly.
export const getDisputesExport = createAsyncThunk(
  `${SLICE_NAME}/get_disputes_export`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const asFiltered = !!payload?.as_filtered
    // Export the columns the user configured (active, in display order), so the CSV
    // matches the table on screen instead of a fixed default set. '#' (number) is
    // always included as the leading column.
    const tableColumns = getState().disputes.table.columns
    const activeFieldIds = (tableColumns || []).filter((c) => c.active).map((c) => c.field_id)
    const columns = ["number", ...activeFieldIds.filter((id) => id !== "number")].join(",")
    let params = { file_name: "Disputes report", columns }
    if (asFiltered) {
      const f = getState().disputes.filters
      params = {
        ...params,
        status: f.status,
        reason_category: expandGroupsToCategories(f.reason_category),
        reason_code: f.reason_code,
        issuer: f.issuer,
        customer: f.customer,
        assigned_to: f.assignee,
        card_type: f.card_type,
        currency: f.currency,
        country: f.country,
        processor: f.processor,
        card_last4: f.card_last4,
        processor_dispute_id: f.processor_dispute_id,
        date_from: f.date_from,
        date_to: f.date_to,
        due_from: f.due_from,
        due_to: f.due_to,
        amount_type: f.amount?.type || undefined,
        amount_min: f.amount?.min ?? undefined,
        amount_max: f.amount?.max ?? undefined
      }
    }
    const response = await services.getDisputesExport(params)
    if (!response.ok || !response.data) {
      // The request asks for a blob, so a refusal comes back as a Blob too and
      // `getApiErrorMessage` re-parses it as JSON — which throws outright when the body
      // is not JSON at all (a gateway HTML page). With no message to resolve,
      // `handleApiError` would notify nothing and the click would look like it silently
      // did nothing, so the local copy goes into the same global channel rather than a
      // second toast.
      let message = null
      try {
        message = await getApiErrorMessage(response)
      } catch {
        message = null
      }
      if (message) await handleApiError(dispatch, response)
      else dispatch(ErrorsActions.errorSave(i18n.t("Export failed. Please try again.")))
      // The raw body is a Blob here — non-serializable, so the resolved message is what
      // travels into the store instead.
      return rejectWithValue(message ?? null)
    }
    FileDownload(response.data, "Disputes report.csv")
    return true
  }
)

// Pull the merchant's real disputes from their connected processors now, then
// refresh the list so the new rows show without a manual reload.
export const syncDisputes = createAsyncThunk(
  `${SLICE_NAME}/sync_disputes`,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const response = await services.syncRecoverDisputes()
    if (!response.ok || !response.data?.ok) {
      // The endpoint's own refusal ("connect Stripe first") is the useful message; the
      // local copy only covers a failure that carries none.
      if (await getApiErrorMessage(response)) await handleApiError(dispatch, response)
      else
        dispatch(
          ErrorsActions.errorSave(
            i18n.t("Sync failed. Make sure Stripe is connected, then try again.")
          )
        )
      return rejectWithValue(response.data)
    }
    const filters = getState().disputes.filters
    dispatch(getDisputes({ filters }))
    return { synced: response.data.synced || 0 }
  }
)

// Invited team members for the Assignee feature. Silent by design: any non-ok response
// (incl. a playground 404) leaves the assignee picker empty rather than telling the
// merchant about a fetch they never asked for.
export const getTeamMembers = createAsyncThunk(
  `${SLICE_NAME}/get_team_members`,
  async (_, { rejectWithValue }) => {
    const response = await services.getTeamMembers()
    if (!response.ok || !response.data) {
      return rejectWithValue(response.data)
    }
    const data = response.data
    return { members: Array.isArray(data) ? data : data.results || [] }
  }
)

export const getReasonCategories = createAsyncThunk(
  `${SLICE_NAME}/get_reason_categories`,
  // Silent: the filter dropdown renders its own error state from `reasonCategories.error`.
  async (_, { rejectWithValue }) => {
    const response = await services.getReasonCategories()
    if (!response.ok || !response.data) {
      return rejectWithValue(response.data)
    }
    return { options: response.data.options ?? [] }
  }
)

// Optimistic: the slice flips the row's pinned flag on `pending` so the icon reacts on
// click, and rolls it back on `rejected`. Reorder happens via the pinTick refetch.
export const pinDispute = createAsyncThunk(
  `${SLICE_NAME}/pin_dispute`,
  async (payload, { dispatch, rejectWithValue }) => {
    const response = await services.pinDispute(payload.id, payload.pin)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { id: payload.id, pin: payload.pin }
  }
)

// Assign (or unassign) a dispute to a team member. The slice already set the row
// optimistically on `pending`. The backend emails the new assignee, which is why
// `emailed` travels back: it is what lets the caller say so or stay quiet about it.
export const assignDispute = createAsyncThunk(`${SLICE_NAME}/assign_dispute`, async (payload) => {
  const { id } = payload ?? {}
  const response = await services.assignDispute(id, payload?.member ? payload.member.id : null)
  // Deliberately resolves on failure. The assign WRITE endpoint isn't wired on every
  // backend yet (the team-member READ is). Rather than roll back and show a hard error for
  // a not-yet-available feature, keep the optimistic assignment and just don't claim an
  // email went out. Drop the `emailed` flag once the endpoint ships — until then a real
  // backend rejection is indistinguishable from the missing endpoint.
  return { id, emailed: response.ok }
})

// Detail-page terminal actions. The list statistics are refreshed on success so the
// All-page cards + sidebar badge stay in step. Silent here: the detail page owns the
// toast + detail reload via the detailAction result.
export const rejectEvidence = createAsyncThunk(
  `${SLICE_NAME}/reject_evidence`,
  async (payload, { dispatch, rejectWithValue }) => {
    const { id, version, notes } = payload ?? {}
    const response = await services.rejectEvidence(id, version, { notes })
    if (!response.ok) {
      return rejectWithValue(response.data)
    }
    dispatch(getDisputesStatistics())
    return { data: response.data ?? null }
  }
)

export const reopenDispute = createAsyncThunk(
  `${SLICE_NAME}/reopen_dispute`,
  async (payload, { dispatch, rejectWithValue }) => {
    const { id } = payload ?? {}
    const response = await services.reopenDispute(id)
    if (!response.ok) {
      return rejectWithValue(response.data)
    }
    dispatch(getDisputesStatistics())
    return { data: response.data ?? null }
  }
)
